import { idbGet, idbSet, idbDelete, idbKeysByPrefix } from './idb';
import { createLogger } from '@/lib/logger';

const log = createLogger('SyncQueue');

const KEY_PREFIX = 'sync_queue:';

export type SyncOperation = 'create' | 'update' | 'delete' | 'save_form';

export interface SyncQueueItem {
  id: string;
  table_name: string;
  record_id: string;
  data: Record<string, unknown>;
  operation: SyncOperation;
  expected_version: number;
  status: 'pending' | 'syncing' | 'failed' | 'completed';
  retries: number;
  created_at: string;
  last_error?: string;
}

function queueKey(baseId: string): string {
  return `${KEY_PREFIX}${baseId}`;
}

export async function queueLoad(baseId: string): Promise<SyncQueueItem[]> {
  if (!baseId) {
    log.warn('queueLoad called without baseId');
    return [];
  }
  return (await idbGet<SyncQueueItem[]>(queueKey(baseId))) ?? [];
}

export async function queueSave(baseId: string, items: SyncQueueItem[]): Promise<void> {
  if (!baseId) {
    log.warn('queueSave called without baseId');
    return;
  }
  await idbSet(queueKey(baseId), items);
}

export async function queueAdd(
  baseId: string,
  item: Omit<SyncQueueItem, 'id' | 'status' | 'retries' | 'created_at'>
): Promise<SyncQueueItem> {
  const items = await queueLoad(baseId);
  const newItem: SyncQueueItem = {
    ...item,
    id: generateQueueId(),
    status: 'pending',
    retries: 0,
    created_at: new Date().toISOString(),
  };
  items.push(newItem);
  await queueSave(baseId, items);
  return newItem;
}

export async function queueGetPending(baseId: string): Promise<SyncQueueItem[]> {
  const items = await queueLoad(baseId);
  return items.filter(i => i.status === 'pending');
}

export async function queueGetFailed(baseId: string): Promise<SyncQueueItem[]> {
  const items = await queueLoad(baseId);
  return items.filter(i => i.status === 'failed');
}

export async function queueUpdate(
  baseId: string,
  id: string,
  updates: Partial<SyncQueueItem>
): Promise<void> {
  const items = await queueLoad(baseId);
  const idx = items.findIndex(i => i.id === id);
  if (idx >= 0) {
    items[idx] = { ...items[idx], ...updates };
    await queueSave(baseId, items);
  }
}

export async function queueRemove(baseId: string, id: string): Promise<void> {
  const items = await queueLoad(baseId);
  const filtered = items.filter(i => i.id !== id);
  await queueSave(baseId, filtered);
}

export async function queueClear(baseId: string): Promise<void> {
  await idbDelete(queueKey(baseId));
}

export async function queueClearAll(): Promise<void> {
  const keys = await idbKeysByPrefix(KEY_PREFIX);
  for (const key of keys) {
    await idbDelete(key);
  }
}

export async function queueList(baseId: string): Promise<SyncQueueItem[]> {
  return queueLoad(baseId);
}

export function generateQueueId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}
